import { getClassLevel } from '../../classIdentity.mjs';
import { getBardicInspirationProfile } from '../../bard.mjs';

// 2024 College of Lore (SRD 5.2.1). Bonus Proficiencies are saved at `classState.bard.bonusProficiencies` and on the Bard's
// occupation entry, where skill checks read them. Magical Discoveries are saved at `classState.bard.magicalDiscoveries` as
// { spells, changedAtLevel } and reach the spell engines as feature grants. Cutting Words is the server's cuttingWords.js;
// `classState.bard.cuttingWords = { ask, revision }` is the player's switch for its prompts.
export const LORE_SUBCLASS_ID = 'lore';
export const BONUS_PROFICIENCIES_FEATURE_ID = 'bard:lore:bonus-proficiencies';
export const CUTTING_WORDS_FEATURE_ID = 'bard:lore:cutting-words';
export const MAGICAL_DISCOVERIES_FEATURE_ID = 'bard:lore:magical-discoveries';
export const PEERLESS_SKILL_FEATURE_ID = 'bard:lore:peerless-skill';
export const BONUS_PROFICIENCY_COUNT = 3;
export const MAGICAL_DISCOVERY_COUNT = 2;
export const CUTTING_WORDS_RANGE_FEET = 60;
export const DISCOVERY_GRANT_PREFIX = 'bard-lore-';
const DISCOVERY_LISTS = Object.freeze(['cleric', 'druid', 'wizard']);
const LEVELS = Object.freeze({ bonusProficiencies: 3, cuttingWords: 3, magicalDiscoveries: 6, peerlessSkill: 14 });

const isLore = character => {
  const state = character?.classState?.bard;
  const raw = state?.subclassId ?? state?.subclass?.id ?? (typeof state?.subclass === 'string' ? state.subclass : null);
  return String(raw || '').trim().toLowerCase() === LORE_SUBCLASS_ID;
};
export const hasLoreFeature = (character, feature) => isLore(character) && getClassLevel(character, 'bard') >= (LEVELS[feature] ?? Infinity);

export const getBonusProficiencies = character => (Array.isArray(character?.classState?.bard?.bonusProficiencies) ? character.classState.bard.bonusProficiencies : []);
export const needsBonusProficiencies = character => hasLoreFeature(character, 'bonusProficiencies') && getBonusProficiencies(character).length < BONUS_PROFICIENCY_COUNT;
/** Why these skills can't be chosen, or ''. `known` lists valid skills and `trained` says whether one is already proficient. */
export const bonusProficienciesProblem = (skills, known, trained) => {
  if (!Array.isArray(skills) || skills.length !== BONUS_PROFICIENCY_COUNT || new Set(skills).size !== BONUS_PROFICIENCY_COUNT || skills.some(skill => !known.includes(skill))) return 'Choose three different skills.';
  return skills.some(trained) ? 'Choose skills you aren’t proficient in.' : '';
};

const isList = value => Array.isArray(value);
export const getMagicalDiscoveries = character => (isList(character?.classState?.bard?.magicalDiscoveries?.spells) ? character.classState.bard.magicalDiscoveries.spells : []);
export const needsMagicalDiscoveries = character => hasLoreFeature(character, 'magicalDiscoveries') && getMagicalDiscoveries(character).length < MAGICAL_DISCOVERY_COUNT;
export const highestBardSpellLevel = level => Math.min(9, Math.ceil(level / 2));
/** A Cleric, Druid or Wizard cantrip, or a spell of a level the Bard has slots for. */
export const isDiscoverySpell = (spell, level) => (spell?.classes || []).some(entry => DISCOVERY_LISTS.includes(String(entry).toLowerCase()))
  && Number.isInteger(Number(spell.level)) && Number(spell.level) <= highestBardSpellLevel(level);
/** One spell can be replaced each time the Bard gains a level. */
export const canChangeMagicalDiscoveries = character => {
  const saved = character?.classState?.bard?.magicalDiscoveries;
  return !saved || Number(saved.changedAtLevel) < getClassLevel(character, 'bard');
};
/** Why these can't be the Magical Discoveries, or ''. */
export const magicalDiscoveriesProblem = (character, ids, catalog) => {
  const level = getClassLevel(character, 'bard');
  if (!isList(ids) || ids.length !== MAGICAL_DISCOVERY_COUNT || new Set(ids).size !== MAGICAL_DISCOVERY_COUNT) return 'Choose two different spells.';
  if (ids.some(id => !isDiscoverySpell(catalog[id], level))) return 'Choose Cleric, Druid or Wizard spells: a cantrip, or a level you have spell slots for.';
  const saved = getMagicalDiscoveries(character);
  if (!saved.length) return '';
  if (!canChangeMagicalDiscoveries(character)) return 'You can replace one of these when you next gain a Bard level.';
  return ids.filter(id => !saved.includes(id)).length > 1 ? 'Replace one spell at a time.' : '';
};
export const discoveryGrantId = spellId => `${DISCOVERY_GRANT_PREFIX}${spellId}`;
export const discoverySpellForGrant = grantId => (String(grantId || '').startsWith(DISCOVERY_GRANT_PREFIX) ? String(grantId).slice(DISCOVERY_GRANT_PREFIX.length) : null);

export const asksCuttingWords = character => character?.classState?.bard?.cuttingWords?.ask !== false;
/** Whether Cutting Words can be offered, Reaction aside: the feature, the switch, and a use left. */
export const canOfferCuttingWords = (character, charismaModifier) => hasLoreFeature(character, 'cuttingWords') && asksCuttingWords(character)
  && getBardicInspirationProfile(character, charismaModifier).remaining > 0;
export const canUsePeerlessSkill = (character, charismaModifier) => hasLoreFeature(character, 'peerlessSkill') && getBardicInspirationProfile(character, charismaModifier).remaining > 0;
export const peerlessSkillDecision = (resolutionId, sides) => ({
  id: `${resolutionId}:${PEERLESS_SKILL_FEATURE_ID}`, featureId: PEERLESS_SKILL_FEATURE_ID, title: 'Peerless Skill',
  prompt: `Your attack roll missed. Roll your d${sides} and add it? The use is spent only if the attack then hits.`,
  choices: [{ id: 'use', label: `Add the d${sides}`, action: 'apply' }, { id: 'skip', label: 'Keep the roll' }],
});
export const peerlessSkillResultDecision = (rollId, total, rolled, sides) => ({
  id: `${rollId}:${PEERLESS_SKILL_FEATURE_ID}:result`, featureId: PEERLESS_SKILL_FEATURE_ID, title: 'Peerless Skill',
  prompt: `Your d${sides} rolled ${rolled}, for a total of ${total}. Did the check succeed? The use is spent only if it did.`,
  choices: [{ id: 'use', label: 'Yes, it succeeded', action: 'apply' }, { id: 'skip', label: 'No, it still failed' }],
});
export const peerlessSkillLabel = sides => `+d${sides} · Peerless Skill (spent only if it works)`;

const lore = Object.freeze({
  id: LORE_SUBCLASS_ID, definitionId: 'bard:lore', classId: 'bard', aliases: Object.freeze(['college of lore']),
  name: 'College of Lore', selectionLevel: 3,
  description: 'Plumb the depths of magical knowledge.',
  features: Object.freeze([
    {
      id: BONUS_PROFICIENCIES_FEATURE_ID, name: 'Bonus Proficiencies', level: 3,
      description: 'You gain proficiency with three skills of your choice.',
      presentation: { kind: 'passive' }, contracts: [],
    },
    {
      id: CUTTING_WORDS_FEATURE_ID, name: 'Cutting Words', level: 3,
      description: 'You learn to use your wit to supernaturally distract, confuse, and otherwise sap the confidence and competence of others. When a creature that you can see within 60 feet of yourself makes a damage roll or succeeds on an ability check or attack roll, you can take a Reaction to expend one use of your Bardic Inspiration; roll your Bardic Inspiration die, and subtract the number rolled from the creature’s roll, reducing the damage or potentially turning the success into a failure.',
      presentation: { kind: 'reaction' }, contracts: [],
    },
    {
      id: MAGICAL_DISCOVERIES_FEATURE_ID, name: 'Magical Discoveries', level: 6,
      description: 'You learn two spells of your choice. These spells can come from the Cleric, Druid, or Wizard spell list or any combination thereof. A spell you choose must be a cantrip or a spell for which you have spell slots, as shown in the Bard Features table. You always have the chosen spells prepared, and whenever you gain a Bard level, you can replace one of the spells with another spell that meets these requirements.',
      presentation: { kind: 'passive' }, contracts: [],
    },
    {
      id: PEERLESS_SKILL_FEATURE_ID, name: 'Peerless Skill', level: 14,
      description: 'When you make an ability check or attack roll and fail, you can expend one use of Bardic Inspiration; roll the Bardic Inspiration die, and add the number rolled to the d20, potentially turning a failure into a success. On a failure, the Bardic Inspiration isn’t expended.',
      presentation: { kind: 'passive' }, contracts: [],
    },
  ]),
});

export default lore;
